import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "grid grid-cols-2 gap-8" }
const _hoisted_2 = { class: "col-span-2 lg:col-span-1" }
const _hoisted_3 = { class: "relative" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("label", {
  id: "status",
  class: "font-basier font-semibold text-dark-gray text-sm"
}, " Status ", -1)
const _hoisted_5 = {
  key: 0,
  class: "absolute bottom-0 text-sm text-red"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("label", {
  id: "comment",
  class: "font-basier font-semibold text-dark-gray text-sm"
}, " Komentarz ", -1)
const _hoisted_7 = { class: "mb-4 flex justify-center lg:justify-end" }
const _hoisted_8 = /*#__PURE__*/_createTextVNode(" Dodaj załączniki ")
const _hoisted_9 = { class: "col-span-2 lg:col-span-1" }
const _hoisted_10 = { class: "inline-block pb-4 font-basier font-semibold text-dark-gray text-sm mb-4 border-b border-b-gray w-full" }
const _hoisted_11 = /*#__PURE__*/_createTextVNode(" Dodane załączniki ")
const _hoisted_12 = {
  key: 0,
  class: "font-bold text-md text-primary"
}
const _hoisted_13 = {
  key: 1,
  class: "text-sm text-red w-full text-center block"
}
const _hoisted_14 = {
  key: 0,
  class: "text-red"
}
const _hoisted_15 = { key: 1 }
const _hoisted_16 = { class: "col-span-2 flex items-center space-x-4 justify-center border-t pt-4 border-t-gray lg:justify-end" }
const _hoisted_17 = /*#__PURE__*/_createTextVNode(" Anuluj ")
const _hoisted_18 = /*#__PURE__*/_createTextVNode(" Zapisz ")

import { ref, watch, toRefs, computed } from 'vue'
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core'
import { UiButton, UiInput, UiSelect, UiFileUpload } from '@/components/common/ui'
import { useDeliveryReactionStatuses } from '@/composables/useDeliveryReactionStatuses'
import { useNotifications } from '@/composables/useNotifications'
import {
    useAdviceDeliveryReactions,
    AdviceDeliveryReactionNewDocument,
} from '@/composables/useAdviceDeliveryReactions'
import { UploadedFileType } from '@/types/partials'
import TheFallback from '../common/TheFallback.vue'
import AdviceDeliveryReactionAddNewDocumentFormAttachmentsList from './AdviceDeliveryReactionAddNewDocumentFormAttachmentsList.vue'

type Props = {
    adviceId: number
}
type Emits = {
    (e: 'cancel'): void
}
enum ERROR_TYPES {
    noDeliveryReactionStatus = 'deliveryReactionStatusId',
    emptyImages = 'emptyImages',
    someImageWithoutType = 'someImageWithoutType',
}


export default /*#__PURE__*/_defineComponent({
  props: {
    adviceId: { type: Number, required: true }
  },
  emits: ["cancel"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'cancel'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    adviceId: number
}




const { fetchDeliveryReactionStatuses, deliveryReactionStatuses } = useDeliveryReactionStatuses()
fetchDeliveryReactionStatuses()

const {
    newDocument,
    loading,
    fetchAdviceDeliveryReactions,
    updateDeliveryReactionNewDocumentsStorage,
    deleteFromDeliveryReactionNewDocumentsStorage,
    addDeliveryReactionDocument,
} = useAdviceDeliveryReactions(props.adviceId)
const initialState = {
    adviceId: props.adviceId,
    comment: '',
    images: [],
}
const shallowNewDocument = ref<AdviceDeliveryReactionNewDocument>({
    ...initialState,
    ...newDocument.value,
})

const hasAttachments = computed(() => shallowNewDocument.value.images.length > 0)
const errorMessage = ref<string>()
const errorType = ref<string>()
watch([shallowNewDocument.value], () => {
    updateDeliveryReactionNewDocumentsStorage(shallowNewDocument.value)
    errorMessage.value = undefined
    errorType.value = undefined
})

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('lg')
const _scrollToTopOfThisFormOnMobile = () => {
    isMobile.value && window.scrollTo({ top: 1000, behavior: 'smooth' })
}
_scrollToTopOfThisFormOnMobile()

const onFileUpload = (files: UploadedFileType[]) => {
    const { images } = toRefs(shallowNewDocument.value)
    files.forEach(file => {
        images.value.push({
            documentTypeId: undefined,
            fileName: file.name,
            base64: file.base64,
            type: file.type,
        })
    })
}

const hasErrors = () => {
    errorMessage.value = undefined
    errorType.value = undefined

    if (!shallowNewDocument.value.deliveryReactionStatusId) {
        errorMessage.value = 'Nie wybrano statusu dokumentu'
        errorType.value = ERROR_TYPES.noDeliveryReactionStatus
        _scrollToTopOfThisFormOnMobile()
        return true
    }

    if (shallowNewDocument.value.images.length === 0) {
        errorMessage.value = 'Dodaj minimum jeden załącznik'
        errorType.value = ERROR_TYPES.emptyImages
        return true
    }

    if (shallowNewDocument.value.images.some(img => !img.documentTypeId)) {
        errorMessage.value = 'Nie wszystkie załączniki mają typ dokumentu'
        errorType.value = ERROR_TYPES.someImageWithoutType
        return true
    }
}

const { pushSuccess, pushFail } = useNotifications()
const handleSubmit = async () => {
    try {
        if (hasErrors()) return

        await addDeliveryReactionDocument(shallowNewDocument.value)
        pushSuccess('Dodano nowy dokument')
        fetchAdviceDeliveryReactions(props.adviceId)
        deleteFromDeliveryReactionNewDocumentsStorage()
    } catch (error) {
        if (error instanceof Error) {
            pushFail('Nie udało się dodać nowego dokumentu')
        }
    }
    emit('cancel')
}

const handleCancel = () => {
    deleteFromDeliveryReactionNewDocumentsStorage()
    emit('cancel')
}

return (_ctx: any,_cache: any) => {
  const _component_IconCross = _resolveComponent("IconCross")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _hoisted_4,
        _createVNode(_unref(UiSelect), {
          modelValue: shallowNewDocument.value.deliveryReactionStatusId,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((shallowNewDocument.value.deliveryReactionStatusId) = $event)),
          options: _unref(deliveryReactionStatuses),
          "aria-labelledby": "status",
          class: _normalizeClass({ 'border-red': errorType.value === 'deliveryReactionStatusId' })
        }, null, 8, ["modelValue", "options", "class"]),
        (errorType.value === ERROR_TYPES.noDeliveryReactionStatus)
          ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(errorMessage.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", null, [
        _hoisted_6,
        _createVNode(_unref(UiInput), {
          modelValue: shallowNewDocument.value.comment,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((shallowNewDocument.value.comment) = $event)),
          type: "textarea",
          rows: "6",
          style: {"resize":"none"},
          "aria-labelledby": "comment"
        }, null, 8, ["modelValue"])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(_unref(UiFileUpload), {
          id: 'images',
          multiple: true,
          "button-props": { primary: true },
          onFilesUpload: onFileUpload
        }, {
          default: _withCtx(() => [
            _hoisted_8,
            _createVNode(_component_IconCross, { class: "ml-2" })
          ]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", null, [
        _createElementVNode("label", _hoisted_10, [
          _hoisted_11,
          (_unref(hasAttachments))
            ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(shallowNewDocument.value.images.length), 1))
            : _createCommentVNode("", true)
        ]),
        (_unref(hasAttachments))
          ? (_openBlock(), _createBlock(AdviceDeliveryReactionAddNewDocumentFormAttachmentsList, {
              key: 0,
              modelValue: shallowNewDocument.value.images,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((shallowNewDocument.value.images) = $event))
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true),
        (errorType.value === ERROR_TYPES.someImageWithoutType)
          ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(errorMessage.value), 1))
          : _createCommentVNode("", true),
        (!_unref(hasAttachments))
          ? (_openBlock(), _createBlock(TheFallback, { key: 2 }, {
              default: _withCtx(() => [
                (errorType.value === ERROR_TYPES.emptyImages)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(errorMessage.value), 1))
                  : (_openBlock(), _createElementBlock("span", _hoisted_15, " Brak załączników "))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_16, [
      _createVNode(_unref(UiButton), { onClick: handleCancel }, {
        default: _withCtx(() => [
          _hoisted_17
        ]),
        _: 1
      }),
      _createVNode(_unref(UiButton), {
        primary: "",
        loading: _unref(loading),
        onClick: handleSubmit
      }, {
        default: _withCtx(() => [
          _hoisted_18
        ]),
        _: 1
      }, 8, ["loading"])
    ])
  ]))
}
}

})